import React, { useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { HiOutlineTrash, HiOutlinePencilSquare, HiOutlinePlus } from 'react-icons/hi2';
import {
  createLocalExpensesCategory,
  updateLocalExpensesCategory,
  deleteLocalExpensesCategory,
} from '../../../../../../../../crud/management_api/local_expenses_categories';
import { ExpenseCategoryType } from '../../../../types';
import { toast } from 'react-toastify';
import './CategoryManager.scss';

interface CategoryManagerProps {
  show: boolean;
  setShow: (show: boolean) => void;
  categories: ExpenseCategoryType[];
  onChanged: () => void;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({
  show,
  setShow,
  categories,
  onChanged,
}) => {
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.warn('Ingrese un nombre para la categoría');
      return;
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user.local_id ? Number(user.local_id) : 0;

    setIsLoading(true);
    try {
      await createLocalExpensesCategory({
        local_id: localId,
        category_name: newName.trim(),
        category_description: newDescription.trim(),
      });
      toast.success('Categoría creada');
      setNewName('');
      setNewDescription('');
      onChanged();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Error al crear categoría');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (categoryId: number) => {
    if (!editName.trim()) {
      toast.warn('El nombre no puede estar vacío');
      return;
    }

    setIsLoading(true);
    try {
      await updateLocalExpensesCategory(categoryId, {
        category_name: editName.trim(),
        category_description: editDescription.trim(),
      });
      toast.success('Categoría actualizada');
      setEditingId(null);
      onChanged();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Error al actualizar categoría');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (categoryId: number, categoryName: string) => {
    if (!window.confirm(`¿Eliminar la categoría "${categoryName}"? Las compras asociadas quedarán sin categoría.`)) {
      return;
    }

    setIsLoading(true);
    try {
      await deleteLocalExpensesCategory(categoryId);
      toast.success('Categoría eliminada');
      onChanged();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Error al eliminar categoría');
    } finally {
      setIsLoading(false);
    }
  };

  const startEdit = (cat: ExpenseCategoryType) => {
    setEditingId(cat.expense_category_id);
    setEditName(cat.category_name);
    setEditDescription(cat.category_description || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditDescription('');
  };

  return (
    <Modal show={show} onHide={() => setShow(false)} centered className="category-manager-modal" container={document.getElementById('root') || undefined}>
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">Gestionar Categorías</Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={() => setShow(false)}>
          <X size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body>
        {/* Create new category */}
        <div className="pb-4 mb-4 border-b border-mp-border">
          <div className="flex flex-col gap-2">
            <div className="flex gap-2 items-center">
              <input
                type="text"
                className="flex-1 px-3 py-2.5 bg-transparent border border-mp-border rounded-mp-md text-mp-base text-mp-text placeholder:text-mp-text-muted/50 focus:outline-none focus:border-mp-orange focus:ring-2 focus:ring-mp-orange/10 transition-all duration-200"
                placeholder="Nombre de categoría"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                disabled={isLoading}
              />
              <button
                className="flex items-center justify-center w-[38px] h-[38px] rounded-mp-md bg-mp-orange text-white shrink-0 transition-opacity duration-200 hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleCreate}
                disabled={isLoading || !newName.trim()}
                style={{ border: 'none' }}
              >
                {isLoading ? <Spinner animation="border" size="sm" /> : <HiOutlinePlus size={18} />}
              </button>
            </div>
            <input
              type="text"
              className="w-full px-3 py-2 bg-transparent border border-mp-border rounded-mp-md text-mp-sm text-mp-text placeholder:text-mp-text-muted/50 focus:outline-none focus:border-mp-orange focus:ring-2 focus:ring-mp-orange/10 transition-all duration-200"
              placeholder="Descripción (opcional)"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Category list */}
        <div className="flex flex-col gap-0.5">
          {categories.length === 0 ? (
            <div className="text-center py-8 px-4 text-mp-text-muted text-mp-sm">
              No hay categorías creadas. Cree una arriba para comenzar a categorizar sus compras.
            </div>
          ) : (
            categories.map((cat) => (
              <div key={cat.expense_category_id} className="rounded-mp-md transition-colors duration-150 hover:bg-mp-text/[0.04]">
                {editingId === cat.expense_category_id ? (
                  <div className="flex flex-col gap-2 p-2.5">
                    <input
                      type="text"
                      className="w-full px-3 py-2 bg-transparent border border-mp-border rounded-mp-md text-mp-sm text-mp-text focus:outline-none focus:border-mp-orange focus:ring-2 focus:ring-mp-orange/10 transition-all duration-200"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdate(cat.expense_category_id);
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      autoFocus
                      disabled={isLoading}
                    />
                    <input
                      type="text"
                      className="w-full px-3 py-2 bg-transparent border border-mp-border rounded-mp-md text-mp-sm text-mp-text placeholder:text-mp-text-muted/50 focus:outline-none focus:border-mp-orange focus:ring-2 focus:ring-mp-orange/10 transition-all duration-200"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Descripción"
                      disabled={isLoading}
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        className="px-3 py-1.5 rounded-md text-mp-xs font-medium bg-transparent text-mp-text-muted border border-mp-border hover:bg-mp-text/[0.06] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={cancelEdit}
                        disabled={isLoading}
                      >
                        Cancelar
                      </button>
                      <button
                        className="px-3 py-1.5 rounded-md text-mp-xs font-medium bg-mp-orange text-white border-0 hover:opacity-85 transition-opacity duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleUpdate(cat.expense_category_id)}
                        disabled={isLoading}
                      >
                        Guardar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between py-2.5 px-3 gap-3">
                    <div className="flex flex-col min-w-0">
                      <span className="text-mp-sm font-medium text-mp-text">{cat.category_name}</span>
                      {cat.category_description && (
                        <span className="text-mp-xs text-mp-text-muted truncate">{cat.category_description}</span>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button
                        className="flex items-center justify-center w-8 h-8 rounded-md bg-transparent text-mp-text-muted border-0 cursor-pointer transition-all duration-150 hover:bg-mp-text/[0.08] hover:text-mp-text disabled:opacity-40 disabled:cursor-not-allowed"
                        onClick={() => startEdit(cat)}
                        disabled={isLoading}
                        title="Editar"
                      >
                        <HiOutlinePencilSquare size={16} />
                      </button>
                      <button
                        className="flex items-center justify-center w-8 h-8 rounded-md bg-transparent text-mp-text-muted border-0 cursor-pointer transition-all duration-150 hover:bg-red-500/10 hover:text-red-500 disabled:opacity-40 disabled:cursor-not-allowed"
                        onClick={() => handleDelete(cat.expense_category_id, cat.category_name)}
                        disabled={isLoading}
                        title="Eliminar"
                      >
                        <HiOutlineTrash size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button variant="none" className="form-button-cancel" onClick={() => setShow(false)}>
            Cerrar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CategoryManager;
